import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import moment from "moment";

import DupontButton from "../../components/DupontButton";
import Header from "../../components/Header";
import PageLoading from "../../components/PageLoading";
import Stepper1 from "../../components/Stepper";
import RA_XLSFileUpload from "../../FileUpload/raXLSFileUpload";
import { Language_Currency_Mapping } from "../home/Settings/settingsOptions.config";

import { APP_SCOPE } from "../../constants/global.constants";

import Footer from "../../common/footer/Footer";

import { ValueStrings } from "../../utils/constantsString";
import {
  extractPlantId,
  getDatasetDetails,
  getSelectedPlantTrainInfo,
} from "../../utils/Utils";

import { API_URLS } from "../../network/appUrls";
import { getLastFileDetails } from "../../network/cipAPI";

import {
  storePlantDetails,
  storeReplacementThreshold,
  storeUnits,
  updateSelectedTrain,
} from "../../Store/TemplateStore/template.action";

import "./index.scss";

import "../../styles/vendors/style";

const steps = (t) => [
  t("stepper.systemInformation"),
  t("stepper.identifyForecast"),
];
const LifetimeAdvisorHome = (props) => {
  const { t, i18n } = useTranslation();
  const xlmRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [isBackClicked, setIsBackClicked] = useState(false);
  const [isNextClicked, setIsNextClicked] = useState(false);
  const [disableNextButton, setDisableNextButton] = useState(false);
  const hasLoaded = useRef(false);
  const { token, plantData } = useSelector((state) => state);

  const renamingText =
    activeStep === 1 ? t("appHome.roAdvisor") : t("ValueStrings.next");
  const handleNext = () => {
    setIsBackClicked(false);
    setIsNextClicked(true);
    if (xlmRef && xlmRef.current && (activeStep === 0 || activeStep === 1)) {
      xlmRef.current.onNextClicked();
      if (activeStep === 1) {
        setActiveStep(activeStep + 1);
      }
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const [isFirstTimeLoad, setIsFirstTimeLoad] = useState(false);
  const [fileDetailsLoaded, setFileDetailsLoaded] = useState(false);
  const [settingsButton, setSettingsButton] = useState(null);

  const handleBack = () => {
    setIsBackClicked(true);
    setIsNextClicked(false);
    if (activeStep - 1 >= 0) setActiveStep(activeStep - 1);
  };
  //const isLocalhost = window.location.hostname === "localhost";
  //const basePath = isLocalhost ? "/" : "/roadvisor/";
  const basePath = API_URLS.basePath;
  const plantId = extractPlantId();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (token && plantData.length > 0 && !hasLoaded.current) {
      hasLoaded.current = true;
      getLastFileDetail();
    }
  }, [token, plantData]);

  useEffect(() => {
    if (activeStep === 2) {
      navigate(`${basePath}cleaningAdvisorHome?plantId=${plantId}`);
    }
  }, [activeStep]);

  const getLastFileDetail = async () => {
    try {
      const trainToUse = getSelectedPlantTrainInfo(plantData, plantId);
      const fileDetails = await getLastFileDetails(trainToUse, {
        overrideAppScope: APP_SCOPE.RA,
      });
      if (fileDetails && fileDetails?.data) {
        setIsFirstTimeLoad(true);
        const data = fileDetails.data;
        const {
          owner_name,
          city,
          country,
          name,
          water_type,
          num_trains,
          tol_num_elements,
          is_dupont_element,
          sel_train,
          sel_pass,
          sel_stage,
          sel_pressure_vessels,
          sel_element_involved,
          file,
          sel_element_type,
          installation_date,
          warranty,
          setting: settings,
          threshold,
        } = data;
        const plantInformation = {
          plantLocation: `${city ? ` ${city}, ${country}` : `${country}`}`,
          plantName: name,
          water_type,
          num_trains,
          tol_num_elements,
          is_dupont_element: is_dupont_element ? "No" : "Yes",
          sel_train,
          sel_pass,
          sel_stage,
          sel_pressure_vessels,
          sel_element_involved,
          sel_element_type,
          installation_date: installation_date
            ? moment(installation_date).format(
                ValueStrings.installationDateFormat,
              )
            : "",
          warranty,
        };
        dispatch(storePlantDetails(plantInformation));
        dispatch(updateSelectedTrain(sel_train));
        dispatch(
          storePlantDetails({
            dataset_name: getDatasetDetails({
              owner_username: owner_name,
              min_date: file?.min_date,
              max_date: file?.max_date,
            }),
          }),
        );

        const unitInformation = {
          temperature: settings?.temperature,
          permeate_quality: settings?.permeate_quality,
          feed_quality: settings?.feed_quality,
          pressure: settings?.pressure,
          flow: settings?.flow,
          volume: settings?.volume,
          mass: settings?.mass,
          currency:
            settings?.currency ||
            parseFloat(Language_Currency_Mapping(i18n.language).currency),
          conversion_rate:
            settings?.conversion_rate ||
            parseFloat(
              Language_Currency_Mapping(i18n.language).conversion_rate,
            ),
          feed_water_permeate_factor: settings?.feed_water_permeate_factor,
          feed_water_factor: settings?.feed_water_factor,
        };
        dispatch(storeUnits(unitInformation));

        const thresholdInformation = {
          max_feed_pressure: threshold?.max_feed_pressure,
          max_permeate_quality: threshold?.max_permeate_quality,
          std_warranty_period: threshold?.std_warranty_period,
          startup_pressure_drop: threshold?.startup_pressure_drop,
          designed_permeate_flow: threshold?.designed_permeate_flow,
          std_membrane_age: threshold?.std_membrane_age,
          max_pressure_drop: threshold?.max_pressure_drop,
          min_permeate_flow: threshold?.min_permeate_flow,
          nwp_trigger: threshold?.nwp_trigger,
          forecast_window: threshold?.forecast_window,
        };
        dispatch(storeReplacementThreshold(thresholdInformation));
        setTimeout(() => {
          setIsFirstTimeLoad(false);
        }, 1000);
      }
    } catch (err) {
      console.log("Error " + err);
    } finally {
      setFileDetailsLoaded(true);
    }
  };

  return (
    <>
      {activeStep !== 2 && (
        <div className="container-main">
          <Header activeStep={activeStep} />
          {/* {activeStep === 0 && (
          <div>
            <Welcome
              message={t("welcomeString.replacementAdvisorIntroduction")}
            ></Welcome>
          </div>
        )} */}
          <div className="main-container">
            <div className="content-container">
              <Stepper1
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                handleBack={handleBack}
                handleNext={handleNext}
                steps={steps}
                renamingText={renamingText}
                disableNextButton={disableNextButton}
              />
              {fileDetailsLoaded ? (
                <div>
                  <RA_XLSFileUpload
                    ref={xlmRef}
                    activeStep={activeStep}
                    setActiveStep={setActiveStep}
                    isFirstTimeLoad={isFirstTimeLoad}
                    isBackClicked={isBackClicked}
                    setIsBackClicked={setIsBackClicked}
                    isNextClicked={isNextClicked}
                    setDisableNextButton={setDisableNextButton}
                    setSettingsButton={setSettingsButton}
                  />
                </div>
              ) : (
                <PageLoading customHeight={"350px"} />
              )}
            </div>
            <div className="advisor-footer-container">
              <div className="button-footer-container">
                <div className="advanced-settings-btn">{settingsButton}</div>
                <div>
                  <DupontButton
                    type="tertiary"
                    onClick={handleBack}
                    // disabled={activeStep === 0}
                  >
                    {activeStep === 0 ? (
                      <Link
                        to={`${basePath}Home?plantId=${plantId}`}
                        className="dupont-btn__tertiary--primary "
                      >
                        &lt; {t("ValueStrings.back")}
                      </Link>
                    ) : (
                      ` < ${t("ValueStrings.back")}`
                    )}
                  </DupontButton>
                  <DupontButton
                    type="tertiary"
                    onClick={handleNext}
                    disabled={activeStep === 1 && disableNextButton}
                  >
                    {renamingText} &gt;
                  </DupontButton>
                </div>
              </div>
              <Footer />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LifetimeAdvisorHome;
